import React, { ReactNode, useEffect, useMemo, useState } from "react";

// UX
import CardDoctorFeeOrderInterfaceUX from "./CardDoctorFeeOrderInterfaceUX";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import SnackMessage from "react-lib/apps/common/SnackMessage";

// Utils
import { Checkbox } from "semantic-ui-react";
import formatComma from "react-lib/utils/formatComma";
import { dateToStringBE } from "react-lib/utils/dateUtils";

// Interface
import { useIntl } from "react-intl";
import {
  CARD_NAME,
  PickedProps,
  RunSequence,
  SetProp,
  State,
  SaveType,
  FilterSearchType,
  WidthsType,
  GenerateTableHeader,
} from "./sequence/DoctorFeeOrderInterface";

// Serializer
import DoctorFeeOrderInterfaceSerializer from "issara-sdk/types/DoctorFeeOrderListPreviewSerializer_apps_DFC";

// Type
type CardDoctorFeeOrderInterfaceProps = {
  onEvent: (e: any) => any;
  setProp: SetProp;
  // seq
  runSequence: RunSequence;
  DoctorFeeOrderInterfaceSequence: State["DoctorFeeOrderInterfaceSequence"];
  languageUX: "en" | "th";
} & PickedProps;

const CardDoctorFeeOrderInterface = (props: CardDoctorFeeOrderInterfaceProps) => {
  const intl = useIntl();

  // useState
  const [openSnackMessage, setOpenSnackMessage] = useState<boolean>(false);
  const [selectedDoctorFee, setSelectedDoctorFee] = useState<any[]>([]);

  // useEffect
  useEffect(() => {
    props.runSequence({ sequence: "DoctorFeeOrderInterface", restart: true });
  }, []);

  useEffect(() => {
    if (props.errorMessage?.[CARD_NAME] || props.successMessage?.[CARD_NAME]) {
      setOpenSnackMessage(true);
    }
  }, [props.errorMessage, props.successMessage]);

  const formatPerformDateTime = (dateTime: any) => {
    if (!dateTime) return;

    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return;

    const pad = (n: number) => n.toString().padStart(2, "0");

    const day = pad(date.getDate());
    const month = pad(date.getMonth() + 1);
    const year = dateTime.slice(0, 4);

    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());

    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  const columnTableCenter = (data: any, style?: any) => {
    return <div style={{ ...style, display: "flex", justifyContent: "center" }}>{data ?? ""}</div>;
  };

  const columnTablePrice = (data: any, style?: any) => {
    return (
      <div style={{ ...style, display: "flex", justifyContent: "flex-end" }}>{data ?? "0.00"}</div>
    );
  };

  const handleCheckAll = (_e: any, v: any) => {
    if (v.checked) {
      let newData = (props.DoctorFeeOrderInterfaceSequence?.DoctorFeeOrderInterfaceList || []).map(
        (item: DoctorFeeOrderInterfaceSerializer) => item.id
      );

      setSelectedDoctorFee(newData);
    } else {
      setSelectedDoctorFee([]);
    }
  };

  const handleSearch = () => {
    props.runSequence({
      sequence: "DoctorFeeOrderInterface",
      action: "SEARCH",
      cardKey: CARD_NAME,
      page: 0,
    });

    setSelectedDoctorFee([]);
  };

  const handleSave = (type: SaveType) => {
    props.runSequence({
      sequence: "DoctorFeeOrderInterface",
      action: "SAVE",
      selectedItems: selectedDoctorFee,
      cardKey: CARD_NAME,
      type,
    });

    setSelectedDoctorFee([]);
  };

  const handleClear = () => {
    props.runSequence({
      sequence: "DoctorFeeOrderInterface",
      action: "CLEAR",
      cardKey: CARD_NAME,
    });

    setSelectedDoctorFee([]);
  };

  const handleChangeData = (_event: any, data: any) => {
    const name = data.name as keyof FilterSearchType;
    props.setProp(`DoctorFeeOrderInterfaceSequence.searchDoctorFeeOrder.${name}`, data?.value);
  };

  const updatePagination = (key: "page" | "pageSize", value: number) => {
    props.setProp(`DoctorFeeOrderInterfaceSequence.paginatedItems.${key}`, value);

    props.runSequence({
      sequence: "DoctorFeeOrderInterface",
      action: "SEARCH",
      cardKey: CARD_NAME,
      [key]: value,
    });

    setSelectedDoctorFee([]);
    props.setProp(`DoctorFeeOrderInterfaceSequence.selectedItems`, []);
  };

  const handleDisabledTrigger = useMemo(() => {
    if (selectedDoctorFee.length === 0) return true;
  }, [selectedDoctorFee, props.DoctorFeeOrderInterfaceSequence?.DoctorFeeOrderInterfaceList]);

  // useMemo
  const doctorFeeOrderInterfaceList = useMemo(() => {
    return (props.DoctorFeeOrderInterfaceSequence?.DoctorFeeOrderInterfaceList || []).map(
      (item: DoctorFeeOrderInterfaceSerializer) => {
        let divisionName = (props.DoctorFeeOrderInterfaceSequence?.divisionOptionsUse || []).find(
          (data: DoctorFeeOrderInterfaceSerializer) => data?.id === item?.requesting_division
        );

        return {
          ...item,
          _checked: columnTableCenter(
            <Checkbox
              checked={selectedDoctorFee.includes(item.id)}
              // disabled={}
              onChange={(e: any, v: any) => {
                if (v.checked) {
                  setSelectedDoctorFee([...selectedDoctorFee, item.id]);
                } else {
                  let newData = selectedDoctorFee.filter((key: any) => key !== item.id);
                  setSelectedDoctorFee(newData);
                }
              }}
            />,
            { padding: "10px" }
          ),
          ...tableHeaders.reduce(
            (data: Partial<Record<keyof DoctorFeeOrderInterfaceSerializer, ReactNode>>, header) => {
              const key = header.accessor;

              if (key === "received_employee_display") {
                data[key] = columnTableCenter((item[key] || "").replace(/\s*\(.*?\)/g, ""));
              } else if (priceColumns.includes(key)) {
                data[key] = columnTablePrice(formatComma(item[key]));
              } else if (key === "medical_record") {
                data[key] = columnTableCenter(item[key] ? `EMR ${item[key]}` : item[key]);
              } else if (dateTimeColumns.includes(key)) {
                data[key] = columnTableCenter(item[key] ? dateToStringBE(item[key]) : item[key]);
              } else if (key === "perform_datetime") {
                data[key] = columnTableCenter(item[key] ? formatPerformDateTime(item[key]) : item[key]);
              } else if (["is_paid", "cancelable", "active"].includes(key)) {
                data[key] = columnTableCenter(item[key] ? "Yes" : "No");
              } else if (key === "remark") {
                data[key] = item[key];
              } else {
                data[key] = columnTableCenter(item[key]);
              }

              return data;
            },
            {}
          ),
        };
      }
    );
  }, [
    selectedDoctorFee,
    props.DoctorFeeOrderInterfaceSequence?.DoctorFeeOrderInterfaceList,
    props.DoctorFeeOrderInterfaceSequence?.divisionOptionsUse,
    tableHeaders,
  ]);

  return (
    <>
      {openSnackMessage && (
        <SnackMessage
          // func
          onEvent={props.onEvent}
          onClose={() => {
            props.setProp(`errorMessage.${CARD_NAME}`, null);
            props.setProp(`successMessage.${CARD_NAME}`, null);
          }}
          error={props.errorMessage?.[CARD_NAME]}
          success={props.successMessage?.[CARD_NAME]}
          languageUX={props.languageUX}
        />
      )}

      <CardDoctorFeeOrderInterfaceUX
        onChangeData={handleChangeData}
        onClearFilter={handleClear}
        searchDoctorFeeOrder={props.DoctorFeeOrderInterfaceSequence?.searchDoctorFeeOrder}
        // table
        tableHeaders={[
          {
            Header: (
              <Checkbox
                checked={
                  selectedDoctorFee.length ===
                    (props.DoctorFeeOrderInterfaceSequence?.DoctorFeeOrderInterfaceList || [])
                      .length &&
                  (props.DoctorFeeOrderInterfaceSequence?.DoctorFeeOrderInterfaceList || [])
                    .length > 0
                }
                onChange={handleCheckAll}
              />
            ),
            accessor: "_checked",
            width: 60,
          },
          ...tableHeaders,
        ]}
        doctorFeeOrderInterfaceList={doctorFeeOrderInterfaceList}
        paginatedItems={props.DoctorFeeOrderInterfaceSequence?.paginatedItems}
        onPageSizeChange={(pageSize: number) => updatePagination("pageSize", pageSize)}
        onPageChange={(page: number) => updatePagination("page", page)}
        searchButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={handleSearch}
            // data
            paramKey={`${CARD_NAME}_SEARCH`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_SEARCH`]}
            // config
            color="blue"
            title={intl.formatMessage({ id: "ค้นหา" })}
            name="SEARCH"
            size="medium"
          />
        }
        triggerButton={
          <ButtonLoadCheck
            // func
            setProp={props.setProp}
            onClick={() => handleSave("TRIGGER")}
            disabled={handleDisabledTrigger}
            // data
            paramKey={`${CARD_NAME}_TRIGGER`}
            buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_TRIGGER`]}
            // config
            color="blue"
            title="Trigger"
            name="TRIGGER"
            size="medium"
          />
        }
      />
    </>
  );
};

CardDoctorFeeOrderInterface.displayName = "CardDoctorFeeOrderInterface";

export default React.memo(CardDoctorFeeOrderInterface);

const dateTimeColumns: Partial<keyof DoctorFeeOrderInterfaceSerializer>[] = [
  "created_at",
  "edited_at",
];

const priceColumns: Partial<keyof DoctorFeeOrderInterfaceSerializer>[] = [
  "compensation_price",
  "final_compensation_price",
  "price_total",
  "price_reimb",
  "price_discount",
  "price_discount",
  "price_copay",
  "price_claimable",
  "price_non_claimable",
];

const columnWidths: WidthsType = {
  id: 70,
  doctor_fee_rule_display: 200,
  quantity: 80,
  medical_record: 120,
  compensation_price: 130,
  final_compensation_price: 130,
  received_employee_display: 200,
  patient_name: 200,
  created_at: 150,
  edited_at: 150,
  perform_datetime: 150,
  remark: 200,
};

const tableHeaders = GenerateTableHeader(columnWidths);
